import { container } from 'tsyringe'
import Action from '@/common/action'
import Denomination from '@/common/denomination'
import {
  BODY_LABEL_ADDON_ANIMATION,
  BODY_LABEL_ADDON_FROM_SLIT,
  BODY_LABEL_ADDON_REVEALED,
  BODY_LABEL_ADDON_ZOOMED_IN,
  ColliderActionType,
  ColliderType,
  MATTER_SCALE,
  MATTER_TO_THREE_SCALE,
  PhysicsLayerZ,
  SpawnLocation
} from '@/features/experience/misc/constants'
import GlobalParticlesManager from '@/features/experience/particle-system/global-particles-manager'
import globalRenderer from '@/shared/misc/global-renderer'
import {
  Asset,
  TokenType,
  IMoveActionData,
  ITransferActionData,
  ContentType,
  ITokenDetail,
  ToolVariant
} from '@/state/vault/domain'
import { Body, Bounds, Vector } from 'matter-js'
import {
  BufferGeometry,
  Color,
  Euler,
  Group,
  Line3,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  PlaneGeometry,
  Quaternion,
  RawShaderMaterial,
  Scene,
  ShaderMaterial,
  SpotLight,
  Vector2,
  Vector3
} from 'three'
import { InputBindingApi } from 'tweakpane/dist/types/blade/input-binding/api/input-binding'
import CoinController from '../coin-controller'
import SceneUtils from '../misc/scene-utils'
import RoomPhysics, { CollisionCategory, CollisionGroup } from './room-physics-base'
import gsap, { Back, Sine, Linear } from 'gsap'
import CoinBgMaterial from '@/features/experience/materials/coin-bg-material'
import { Howl } from 'howler'
import { Id } from '@/common/base-types'
import { DebugFlag } from '@/common/constants'
import { Interface } from '@/shared/di/interface'
import type { IDebug } from '@/shared/di/interface/debug'
import type { ISize } from '@/shared/di/interface/size'
import type { IAssets, LoadingItem } from '@/shared/di/interface/assets'
import type { ISound } from '@/shared/di/interface/sound'

import BaseController from '../base-controller'
import EnvironentEditor from '@/features/environment-editor/environment-editor'
import commonUtils from '@/common/common-utils'
import TingController from '../ting-controller'

import bubblePool from '../misc/bubble-pool'

const sound = container.resolve<ISound>(Interface.ISound)

export enum StaggerType {
  NONE = 'staggerNone',
  FIRST = 'staggerFirst',
  ALL = 'staggerAll'
}

const tempQuat = new Quaternion()
const tempEuler = new Euler()
const tempVector3 = new Vector3()
const tempColor = new Color()
const RADIANS = Math.PI / 180

export enum RoomConnectionSlot {
  Left = 'left',
  Right = 'right',
  Top = 'top',
  Bottom = 'bottom',
  Combine = 'combine',
  Divide = 'divide'
}

export interface RoomConnection {
  id: string
  slot: RoomConnectionSlot
}

interface RoomConfig {
  id: string
  hasExchange: boolean
  hasContacts: boolean
  showMenu: boolean
  canZoomCoin: boolean
  modelURL: string
  cameraFov: number
  cameraNear: number
  cameraFar: number
  cameraPosition: Vector3
  entrenceSlitManifest?: LoadingItem[]
  connectedRooms: RoomConnection[]
  roomHeight: number
}

interface SpawnQueueItem {
  controller: BaseController
  point: Vector
  velocity: Vector
  spawnLocation: SpawnLocation
  randomFactor: number
  index?: number
}
export default abstract class RoomBase {
  config: RoomConfig
  scene: Scene = new Scene()
  physics: RoomPhysics
  particleManager: GlobalParticlesManager | undefined
  tokenControllers: BaseController[] = []
  coinMeshes: Mesh[] = []
  camera: PerspectiveCamera = {} as PerspectiveCamera
  cameraPivot = new Object3D()
  visible = false
  sceneTriggerColliders: Object3D[] = []
  coinZoomedIn = false
  onCoinZoomedIn = new Action<ITokenDetail | undefined>()

  onTubeClosed = new Action<void>()
  onCoinRemove = new Action<string>()
  onCoinChangeRoom = new Action<IMoveActionData>()
  onTokenEnteredTube = new Action<ITransferActionData>()
  onNewAssetsArrived = new Action<Asset[]>()
  onCoinsCombine = new Action<Asset[]>()
  onCoinDivide = new Action<Asset[]>()
  onUpdateBloomList = new Action<void>()
  onRevealStarted = new Action<void>()
  onRevealItemAdded = new Action<Asset>()
  onTokenDispose = new Action<string>()

  #tweakpaneControllers: InputBindingApi<unknown, number>[] = []
  extraCameraOffsetZ = 0
  #timeSinceLastFlushItem = 0
  #firstTimeActivated = false
  #lockFromInput = false
  #slitLightDistanceLine = new Line3()
  #nearSlitSound: Howl | undefined
  #spawnWaitingList: SpawnQueueItem[] = []
  protected coinContainer = new Group()
  #zoomCoinBackgroundDim: Mesh
  #zoomCoinDimMaterial = new MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0,
    depthWrite: true,
    depthTest: true
  })
  #zoomCoinBackground: Mesh
  #zoomCoinBackgroundMaterial: RawShaderMaterial
  #isConnected = false
  slitRoofPosition: Vector2
  slitFloorPosition: Vector2
  tubePosition: Vector2
  light: SpotLight

  protected activeContactWalletId = ''
  userName = ''
  userId = ''
  walletId = ''
  walletFaceId = ''
  isDestroyed = false
  isActive = false
  envMapEditor: EnvironentEditor | undefined

  constructor(config: RoomConfig, physics: RoomPhysics) {
    this.physics = physics
    this.config = config

    this.slitFloorPosition = new Vector2(0, 0)
    this.slitRoofPosition = new Vector2(0, -this.config.roomHeight * 0.8 * 100)
    this.tubePosition = new Vector2(0, this.config.roomHeight - 4.2)

    globalRenderer.getRenderer().localClippingEnabled = false

    const light = new SpotLight(0xffffff, 0)
    this.light = light
    this.light.position.set(-4, 10, 14)
    this.light.target.position.set(0, 10, 0)
    this.light.lookAt(this.light.target.position)
    this.scene.add(this.light.target)

    light.castShadow = false

    this.scene.add(light)

    this.#zoomCoinBackgroundMaterial = CoinBgMaterial.getMaterial()
    this.#zoomCoinBackground = new Mesh(new PlaneGeometry(25, 25), this.#zoomCoinBackgroundMaterial)
    this.#zoomCoinBackground.position.y = this.config.roomHeight * 0.45

    //position off screen
    this.#zoomCoinBackground.position.x = 1000000
    this.#zoomCoinBackground.position.z = 18.1
    this.#zoomCoinBackground.renderOrder = 80
    this.#zoomCoinBackground.userData.enableBloom = false

    this.#zoomCoinBackgroundDim = new Mesh(new PlaneGeometry(40, 40), this.#zoomCoinDimMaterial)
    this.#zoomCoinBackgroundDim.position.copy(this.#zoomCoinBackground.position)
    this.#zoomCoinBackgroundDim.position.z = 17

    this.#zoomCoinBackgroundDim.userData.enableBloom = false
    this.#zoomCoinBackgroundDim.renderOrder = 0

    if (this.config.id === 'wallet') {
      this.#zoomCoinBackground.position.y = this.config.roomHeight * 0.4
    } else {
      this.#zoomCoinBackground.position.y = this.config.roomHeight * 0.44
      this.#zoomCoinBackground.rotation.x = -0.5
      this.#zoomCoinBackgroundDim.rotation.x = -0.5
    }

    if (config.canZoomCoin) {
      this.scene.add(this.#zoomCoinBackgroundDim)
      this.scene.add(this.#zoomCoinBackground)
    }

    this.camera = new PerspectiveCamera(
      this.config.cameraFov,
      window.innerWidth / window.innerHeight,
      this.config.cameraNear,
      this.config.cameraFar
    )

    this.cameraPivot.position.copy(this.config.cameraPosition)
    this.cameraPivot.add(this.camera)
    this.scene.add(this.cameraPivot)
    this.physics.setCamera(this.camera)
    this.physics.setRoom(this)

    const assets = container.resolve<IAssets>(Interface.IAssets)
    const closeTubeRaycastPlane = new Mesh(new PlaneGeometry(7, 3, 1, 1), assets.getMaterial('hitBox'))
    closeTubeRaycastPlane.userData.hideFromBloom = false
    closeTubeRaycastPlane.userData.type = ColliderType.ACTION
    closeTubeRaycastPlane.userData.data = ColliderActionType.CLOSE_TUBE
    closeTubeRaycastPlane.position.copy(SceneUtils.matterToSceneAtZ0(this.slitRoofPosition))
    closeTubeRaycastPlane.position.y += 1
    closeTubeRaycastPlane.position.z += 1
    this.scene.add(closeTubeRaycastPlane)
    this.sceneTriggerColliders.push(closeTubeRaycastPlane)
  }

  setActiveContactId(id: string) {
    this.activeContactWalletId = id
  }

  setUserName(name: string) {
    this.userName = name
  }

  setUserId(id: string) {
    this.userId = id
  }

  setConnectionReady(status: boolean) {
    this.#isConnected = status
    this.physics.interactions.setConnectionReady(status)
  }

  registerSpace(module: RoomBase) {}

  getAdjustedManifest(): LoadingItem[] {
    return []
  }

  async init(): Promise<void> {
    this.physics.init()
    this.addInteractionListeners()
    this.setupScene()

    this.envMapEditor = new EnvironentEditor()
    this.envMapEditor.onEnvMapUpdated.add(this.onEnvMapUpdated)
    const envMap = this.envMapEditor.getEnvMap()

    const assets = container.resolve<IAssets>(Interface.IAssets)
    assets.addTexture('envMap', envMap, 'global')
    this.scene.environment = envMap

    return Promise.resolve()
  }

  onEnvMapUpdated = () => {
    const assets = container.resolve<IAssets>(Interface.IAssets)
    const envMap = this.envMapEditor!.getEnvMap()
    assets.addTexture('envMap', envMap, 'global')
    this.scene.environment = envMap
  }

  createParticleSystem() {
    this.particleManager = new GlobalParticlesManager()

    this.scene.add(this.particleManager.particleSystem)
  }

  addInteractionListeners() {
    this.physics.interactions.onTokenEnteredSlit.add(e => {
      const tc = this.getTokenControllerByBodyId(e.coinId)

      let toRoom = ''
      const slot = e.slot

      const outgoingConnection = this.config.connectedRooms.find(c => c.slot === slot)
      if (outgoingConnection) {
        toRoom = outgoingConnection.id
      }

      if (tc && toRoom !== '') {
        this.onCoinChangeRoom.invoke({
          fromRoom: this.config.id,
          toRoom: toRoom,
          slot: slot,
          asset: tc.asset
        })

        this.assetRemoveController(tc)
      }
    })

    this.physics.interactions.onTokenTapped.add(e => {
      const tc = this.tokenControllers.find(c => c.id === e.id)
      if (!tc) {
        return
      }

      tc.release()

      const doubleTap = tc.timeSinceLastPress < 200

      if (doubleTap) {
        if (tc.singleTapTween) {
          tc.singleTapTween.kill()
          tc.singleTapTween = undefined
        }

        //if (container.resolve<IDebug>(Interface.IDebug).getFlag(DebugFlag.DoubleTapToZoom)) {
        this.openCoin(tc)

        gsap.to(tc.mesh.scale, {
          duration: 0.3,
          ease: Sine.easeInOut,
          x: tc.meshInitScale,
          y: tc.meshInitScale,
          z: tc.meshInitScale
        })
        //}
      } else {
        if (Date.now() - tc.pressedTime < 200) {
          if (
            this.#isConnected &&
            tc.asset.t === TokenType.Ephemeral &&
            (tc.asset.c as ContentType) === ContentType.Emoji
          ) {
            tc.singleTapTween = gsap.to(tc.mesh.scale, {
              delay: 0.2,
              duration: 0.3,
              x: tc.meshInitScale * 2,
              y: tc.meshInitScale * 2,
              z: tc.meshInitScale * 2,
              ease: Sine.easeIn,
              onStart: () => {
                sound.play('bubble-burst-swoosh')
              },
              onComplete: () => {
                if (tc.singleTapTween) {
                  this.burstBubble(tc)

                  tc.singleTapTween = undefined
                }
              }
            })
          }
        }
      }
    })

    this.physics.interactions.onTokenDropped.add(e => {
      const tc = this.tokenControllers.find(c => c.id === e.id)
      if (!tc) {
        return
      }
      tc.mesh.userData.dragged = false
      tc.isLifted = false
      tc.animateInBody()

      this.updateSaveGoals()

      if (tc.asset.t === TokenType.Coin) {
        const cc = tc as CoinController
        let coinType = 1
        if (cc.denomination <= 1000) {
          coinType = 1
        } else if (cc.denomination <= 10000) {
          coinType = 10
        } else {
          coinType = 100
        }
        const sound = container.resolve<ISound>(Interface.ISound)
        sound.play('collide_coin' + coinType + '_' + Math.ceil(Math.random() * 4))
      } else if (tc.asset.t === TokenType.Ephemeral && (tc.asset.c as ContentType) === ContentType.Emoji) {
        const playSnd = sound.play('tube_exit_bubble_1', 0.1)
        playSnd?.rate(0.4)
      }
    })

    this.physics.interactions.onTokenPinched.add(e => {
      const tc = this.tokenControllers.find(c => c.id === e.id)

      if (tc) {
        this.openCoin(tc)
      }
    })

    this.physics.interactions.onTokenLifted.add(e => {
      const tc = this.tokenControllers.find(c => c.id === e.id)

      if (!tc) {
        return
      }

      tc.press()

      tc.isLifted = true
      tc.mesh.userData.dragged = true

      this.updateSaveGoals()

      if (tc.asset.t === TokenType.Coin) {
        const cc = tc as CoinController
        const particleCount = 20

        const radius = Math.abs((cc.body.bounds.max.x - cc.body.bounds.min.x) * 0.5) * 0.01
        for (let index = 0; index < particleCount; index++) {
          const x = radius * Math.cos((2 * Math.PI * index) / particleCount)
          const y = radius * Math.sin((2 * Math.PI * index) / particleCount)
          const circlePos = new Vector3(x, y, 0)

          this.particleManager?.spawnParticle({
            position: SceneUtils.getBodyPositionInScene(cc.body).add(circlePos),
            velocity: tempVector3.set(0, 0, 10),
            size: 1.5,
            lifetime: 1,
            color: tempColor.set(cc.baseColor),
            velocityRandomness: 1,
            positionRandomness: 0,
            turbulence: 0.6
          })
        }

        let coinType = 1
        if (cc.denomination <= 900) {
          coinType = 1
        } else if (cc.denomination <= 9000) {
          coinType = 10
        } else {
          coinType = 100
        }

        const sound = container.resolve<ISound>(Interface.ISound)
        sound.play('touch_coin_' + cc.denomination / 100)

        const extraLayer = Math.ceil(Math.random() * 3)
        if (extraLayer > 1) {
          sound.play('touch_coin' + coinType + '_' + extraLayer)
        }
      } else if (tc.asset.t === TokenType.Ephemeral && (tc.asset.c as ContentType) === ContentType.Emoji) {
        const playSnd = sound.play('tube_exit_bubble_1', 0.1)
        playSnd?.rate(0.4)
      }

      tc.animateInBody()
    })

    this.physics.interactions.onTokenEnteredTube.add(e => {
      const cc = this.tokenControllers.find(c => c.id === e.id)

      if (cc) {
        cc.asset.ux.user.velx = cc.body.velocity.x

        this.onTokenEnteredTube.invoke({
          id: cc.assetId,
          toVault: ''
        })

        this.assetRemoveController(cc)
      }
    })
  }

  burstBubble(tc: BaseController) {
    const pos = tc.mesh.position.clone()

    this.onTokenDispose.invoke(tc.asset.id)
    this.assetRemoveController(tc)
    this.physics.interactions.removeFromDropWaitingList(tc.body)

    gsap.delayedCall(0.2, () => {
      sound.play('tube_exit_bubble_1')
    })

    const bubbleCount = 6
    for (let index = 0; index < bubbleCount; index++) {
      const bubble = bubblePool.get()

      const startScale = 0.1 + 0.6 * Math.random()

      bubble.scale.setScalar(startScale)
      bubble.rotation.z = 0
      bubble.position.copy(pos)

      const mat = bubble.material as ShaderMaterial
      mat.uniforms.cameraPosition.value.copy(this.cameraPivot.position)

      this.scene.add(bubble)

      const x = Math.cos((2 * Math.PI * index) / bubbleCount)
      const y = Math.sin((2 * Math.PI * index) / bubbleCount)
      const dir = new Vector3(x, y, 0)

      bubble.position.add(dir.multiplyScalar(Math.random() + 0.3))
      const to = pos.clone().add(dir.clone().multiplyScalar(2))

      const duration = 0.2 + 0.5 * Math.random()
      gsap.to(bubble.position, {
        duration,
        x: to.x,
        y: to.y,
        z: to.z
      })
      const toScale = Math.max(0.0001, startScale - 0.3)
      gsap.to(bubble.scale, {
        duration: duration * 0.8 + 0.4 * Math.random(),
        x: toScale,
        y: toScale,
        z: toScale,
        ease: Sine.easeOut,
        onComplete: () => {
          this.scene.remove(bubble)
          bubblePool.release(bubble)
          const bubbleSnd = sound.play('tube_exit_bubble_1')
          bubbleSnd?.rate(2.2 - Math.random() * 0.4)
          bubbleSnd?.volume(Math.random() * 0.2)
        }
      })
      gsap.to(bubble.rotation, {
        duration: duration * 0.8 + 1.4 * Math.random(),
        z: 1,
        ease: Sine.easeOut
      })
    }
  }

  //override by room
  animateCoinFromActiveTube() {}

  //override by room
  handleRaycastHit(triggerData: any) {}

  snapshotCoinPositions() {
    this.tokenControllers.forEach(cc => {
      cc.asset.ux.user.posx = Math.round(cc.body.position.x)
      cc.asset.ux.user.posy = Math.round(cc.body.position.y)
      cc.asset.ux.user.rot = Math.round(cc.body.angle * 100) / 100
    })
  }

  updateSaveGoals() {
    this.tokenControllers.forEach(tc => {
      tc.body.plugin.gravityParentId = null
    })

    //update save goal
    this.tokenControllers
      .filter(
        tc =>
          tc.asset.c === String(ContentType.Tool).toLowerCase() && tc.asset.va === ToolVariant.Savegoal && tc.isLifted
      )
      .forEach(tc => {
        let saveGoalSum = 0
        this.tokenControllers
          .filter(tc => tc.asset.t === TokenType.Coin)
          .sort((a, b) => (a.asset.d! < b.asset.d! ? 1 : -1))
          .forEach(tc2 => {
            if (tc.assetId !== tc2.assetId && tc2.body.plugin.gravityParentId === null) {
              if (saveGoalSum < Number(tc.asset.ux.user.goal) * 100) {
                saveGoalSum += tc2.asset.d!
                tc2.body.plugin.gravityParentId = tc.body.id
              }
            }
          })
      })
  }

  openCoin(tokenController: BaseController) {
    if (!this.config.canZoomCoin) {
      return
    }

    tokenController.body.label += BODY_LABEL_ADDON_ZOOMED_IN
    this.onCoinZoomedIn.invoke({
      color: '#' + tokenController.baseColor.toString(16),
      asset: tokenController.asset
    })
    this.#zoomCoinBackgroundMaterial.uniforms.time.value = 0
    this.#zoomCoinBackground.position.x = 0
    this.#zoomCoinBackgroundDim.position.x = 0
    this.#zoomCoinBackgroundMaterial.uniforms.color1.value.set(tokenController.baseColor)

    this.physics.inactivate()
    const size = container.resolve<ISize>(Interface.ISize).getSize()
    const pos = SceneUtils.screenToSceneSpace(this.camera, new Vector2(size.width * 0.5, size.height * 0.5), 1)
    pos.z = 20
    pos.y *= -1
    this.coinZoomedIn = true
    this.#lockFromInput = true

    const cc = tokenController as CoinController
    if (cc.zoomIn) {
      cc.zoomIn(pos).then(() => {
        this.#lockFromInput = false
        const animObj = { progress: 0 }
        gsap.to(animObj, {
          progress: 1,
          duration: 0.3,
          onUpdate: () => {
            this.#zoomCoinDimMaterial.opacity = animObj.progress * 0.97
            this.#zoomCoinBackgroundMaterial.uniforms.opacity.value = animObj.progress
          }
        })
      })
    }
  }

  closeOpenCoins() {
    if (this.#lockFromInput || !this.coinZoomedIn) {
      return
    }

    this.onCoinZoomedIn.invoke(undefined)

    this.coinZoomedIn = false
    this.physics.activate()

    const cc = this.tokenControllers.find(c => c.isZoomed) as CoinController

    if (cc) {
      cc.animateInBody()

      Body.setPosition(cc.body, new Vector2(0, this.config.roomHeight * MATTER_SCALE * -0.5))
      Body.setVelocity(cc.body, new Vector2(0, 0))

      cc.zoomOut(
        () => {
          this.physics.interactions.addForceToMakeRoom(cc.body)
        },
        () => {
          this.physics.interactions.addToDropWaitingList(cc.body)
          cc.body.label = cc.body.label.replace(BODY_LABEL_ADDON_ZOOMED_IN, '')
          //this.updateSaveGoals()
        }
      )

      const animObj = { progress: this.#zoomCoinBackgroundMaterial.uniforms.opacity.value }
      gsap.to(animObj, {
        progress: 0,
        duration: 0.3,
        onUpdate: () => {
          this.#zoomCoinBackgroundMaterial.uniforms.opacity.value = animObj.progress
          this.#zoomCoinDimMaterial.opacity = animObj.progress
        },
        onComplete: () => {
          this.#zoomCoinBackground.position.x = 100000
          this.#zoomCoinBackgroundDim.position.x = 100000
        }
      })
    }
  }

  removeInteractionListeners() {
    this.physics.interactions.onTokenEnteredSlit.removeAll()
    this.physics.interactions.onTokenTapped.removeAll()
    this.physics.interactions.onTokenDropped.removeAll()
    this.physics.interactions.onTokenLifted.removeAll()
    this.physics.interactions.onTokenEnteredTube.removeAll()
  }

  removeAllTubes() {}

  setupScene() {
    this.scene.background = new Color(0x000000)
    this.scene.add(this.coinContainer)
  }

  activate(): void {
    if (this.isActive) {
      return
    }

    this.isActive = true

    this.physics.activate()
    if (!this.#firstTimeActivated) {
      this.#firstTimeActivated = true

      this.tokenControllers.forEach(x => {
        x.animateInBody()
      })
    }

    try {
      globalRenderer.getRenderer().compile(this.scene, this.camera)
    } catch (err: any) {
      console.log('could not compile shaders')
    }

    const sound = container.resolve<ISound>(Interface.ISound)

    this.#nearSlitSound = sound.startLoop('slit_ambience_1')
    this.#nearSlitSound.volume(0)

    this.flushWaitingList(StaggerType.ALL, true)
  }

  inactivate(): void {
    if (!this.isActive) {
      return
    }

    this.isActive = false

    this.physics.inactivate()
    if (!commonUtils.isProdEnv && container.resolve<IDebug>(Interface.IDebug).isDebug) {
      this.#tweakpaneControllers.forEach(c => {
        c.dispose()
      })
    }
    this.#nearSlitSound?.stop()

    //this.onCoinZoomedIn.removeAll()
  }

  activateComplete() {}

  flushWaitingList(stagger: StaggerType = StaggerType.NONE, skipTransferedAssets = false, revealAnimation = false) {
    const newAssets: Asset[] = []

    const allowedItems = this.#spawnWaitingList.filter(item => {
      const asset = (item.controller as CoinController).asset
      if (asset && asset.ux.user.fromWalletId && skipTransferedAssets) {
        return false
      }
      if (asset.ux.user.fromWalletId) {
        if (asset.ux.user.fromWalletId === this.activeContactWalletId) {
          newAssets.push(asset)
          return true
        }
      } else {
        return true
      }
      return false
    })

    allowedItems.forEach((queueItem: SpawnQueueItem, index: number) => {
      queueItem.controller.body.label += BODY_LABEL_ADDON_FROM_SLIT
      queueItem.index = index
      if (stagger === StaggerType.NONE) {
        this.doFlushItem(queueItem, revealAnimation)
      } else if (stagger === StaggerType.ALL) {
        window.setTimeout(() => {
          this.doFlushItem(queueItem, revealAnimation)
        }, index * 200 + 100)
      }

      this.#spawnWaitingList.splice(
        this.#spawnWaitingList.findIndex(itemInOriginalQueue => itemInOriginalQueue === queueItem),
        1
      )
    })

    if (allowedItems.length > 0) {
      this.onNewAssetsArrived.invoke(newAssets)

      if (revealAnimation) {
        sound.play('reveal-swoosh')
      } else {
        this.playFlushSound(allowedItems)
      }

      /*window.setTimeout(() => {
        this.onNewAssetsArrived.invoke(newAssets)
        allowedItems.length = 0
      }, allowedItems.length * 100 + 500)
      */
    }
  }

  playFlushSound(items: SpawnQueueItem[]) {
    const coinsFromSlit = items.filter(
      item =>
        item.spawnLocation === SpawnLocation.BottomSlit ||
        item.spawnLocation === SpawnLocation.TopSlit ||
        item.spawnLocation === SpawnLocation.LeftSlit ||
        item.spawnLocation === SpawnLocation.RightSlit ||
        item.spawnLocation === SpawnLocation.WalletCombine ||
        item.spawnLocation === SpawnLocation.WalletDivide ||
        item.spawnLocation === SpawnLocation.Transfer
    )

    const sound = container.resolve<ISound>(Interface.ISound)
    /*if (coinsFromSlit.length > 0) {
      sound.play('slit_enter')?.rate(0.8 + Math.random() * 0.4)
    }*/

    if (coinsFromSlit.length > 0) {
      if (coinsFromSlit.length < 3) {
        sound.play('slit_exit_single')
      } else if (coinsFromSlit.length < 6) {
        sound.play('slit_exit_few')
      } else {
        sound.play('slit_exit_many')
      }
    }

    const coinsFromTube = items.filter(
      item => item.spawnLocation === SpawnLocation.Tube || item.spawnLocation === SpawnLocation.Center
    )
    if (coinsFromTube.length > 0) {
      if (
        coinsFromTube.find(
          i => i.controller.asset.t === TokenType.Ephemeral && i.controller.asset.c === ContentType.Emoji
        )
      ) {
        sound.play('tube_exit_bubble_1')
      } else {
        if (coinsFromTube.length < 3) {
          sound.play('tube_exit_1')
        } else if (coinsFromTube.length < 6) {
          sound.play('tube_exit_2')
        } else {
          sound.play('tube_exit_3')
        }
      }
    }
  }

  doFlushItem(queueItem: SpawnQueueItem, useRevealAnimation = false) {
    //don't collide with other coins at all
    queueItem.controller.body.collisionFilter.group = 0 //CollisionGroup.ActiveCoins
    queueItem.controller.body.collisionFilter.mask = 0 //CollisionCategory.StaticWallsFront

    if (useRevealAnimation) {
      this.tweenedAnimationReveal(queueItem)
    } else {
      queueItem.controller.enable()

      this.physics.add(queueItem.controller.body)
      this.physics.interactions.addToDropWaitingList(queueItem.controller.body)

      switch (queueItem.spawnLocation) {
        case SpawnLocation.TopSlit:
        case SpawnLocation.BottomSlit:
        case SpawnLocation.Tube:
        case SpawnLocation.TransferParentSlit:
          if (queueItem.controller.body.area > 80000) {
            queueItem.velocity.x += (Math.random() * 5 - 0.55) * queueItem.randomFactor
          } else {
            queueItem.velocity.x += (Math.random() * 40 - 20) * queueItem.randomFactor
          }

          queueItem.velocity.y += Math.random() * 40 - 20 * queueItem.randomFactor

          break
      }

      Body.setVelocity(queueItem.controller.body, queueItem.velocity)
    }

    this.onUpdateBloomList.invoke()

    this.#timeSinceLastFlushItem = Date.now()

    //emit particles
    for (let index = 0; index < 20; index++) {
      this.particleManager?.spawnParticle({
        position: SceneUtils.getBodyPositionInScene(queueItem.controller.body),
        velocity: tempVector3.set(queueItem.velocity.x, -queueItem.velocity.y, 0).multiplyScalar(0.1),
        size: 2.5,
        lifetime: 1,
        color: tempColor.set(
          (queueItem.controller as CoinController).baseColor
            ? (queueItem.controller as CoinController).baseColor
            : 0xffffff
        ),
        velocityRandomness: 1,
        positionRandomness: 2.7,
        turbulence: 0.6
      })
    }
  }

  tweenedAnimationReveal(queueItem: SpawnQueueItem) {
    this.onRevealStarted.invoke()

    queueItem.controller.isAnimated = true
    queueItem.controller.mesh.visible = true
    const pos = {
      x: queueItem.controller.body.position.x * MATTER_TO_THREE_SCALE,
      y: -queueItem.controller.body.position.y * MATTER_TO_THREE_SCALE,
      z: PhysicsLayerZ.Front
    }

    queueItem.controller.mesh.position.z = PhysicsLayerZ.Front
    queueItem.controller.mesh.rotation.x = Math.PI * 1.5
    queueItem.controller.mesh.rotation.y = Math.random() * Math.PI

    const timeline = gsap.timeline({
      onComplete: () => {
        queueItem.controller.body.label += BODY_LABEL_ADDON_REVEALED
        queueItem.controller.isAnimated = false
        queueItem.controller.setPosition(pos.x * MATTER_SCALE, -pos.y * MATTER_SCALE)
        this.physics.add(queueItem.controller.body)
        queueItem.controller.animateInBody()
        queueItem.controller.enable()

        queueItem.controller.body.collisionFilter.group = CollisionGroup.ActiveCoins
        queueItem.controller.body.collisionFilter.mask = CollisionCategory.None
        queueItem.controller.body.label = queueItem.controller.body.label.replace('BODY_LABEL_ADDON_FROM_SLIT', '')

        queueItem.controller.addGlowSprite()
        this.onUpdateBloomList.invoke()
      },
      onUpdate: () => {
        if (Math.random() > 0.9) {
          this.particleManager?.spawnParticle({
            position: queueItem.controller.mesh.position,
            velocity: tempVector3
              .copy(queueItem.controller.mesh.position)
              .sub(queueItem.controller.lastPosition)
              .normalize(),
            size: 3.5,
            lifetime: 1,
            color: tempColor.set(
              (queueItem.controller as CoinController).baseColor
                ? (queueItem.controller as CoinController).baseColor
                : 0xffffff
            ),
            velocityRandomness: 0.3,
            positionRandomness: 0.7,
            turbulence: 0.5
          })
        }
      }
    })

    timeline.add(
      gsap.to(pos, {
        duration: 1,
        ease: Back.easeOut,
        y: 6.5 + 4 * Math.random(),
        onStart: () => {
          if (queueItem.controller.asset.c === ContentType.Emoji) {
            sound.play('tube_exit_bubble_1')
          } else {
            sound
              .play('coin-reveal-sprite', 0.2, 'variant' + Math.ceil(Math.random() * 5))
              ?.rate(0.8 + Math.random() * 0.4)
          }
        },
        onUpdate: () => {
          queueItem.controller.mesh.position.y = pos.y
        }
      }),
      0
    )

    //z
    timeline.add(
      gsap.to(pos, {
        duration: 0.6,
        z: 10,
        ease: Sine.easeIn,
        onUpdate: () => {
          queueItem.controller.mesh.position.z = pos.z
        }
      }),
      0.2
    )

    timeline.add(
      gsap.to(pos, {
        duration: 0.5,
        z: 20,
        ease: Linear.easeNone,
        onUpdate: () => {
          queueItem.controller.mesh.position.z = pos.z
        }
      }),
      0.7
    )

    timeline.add(
      gsap.to(pos, {
        duration: 1.0,
        z: PhysicsLayerZ.Revealed + (queueItem.index ? 0.2 * queueItem.index : 0),
        ease: Sine.easeOut,
        onUpdate: () => {
          queueItem.controller.mesh.position.z = pos.z
        }
      }),
      1.3
    )

    //x
    timeline.add(
      gsap.to(pos, {
        duration: 0.7,
        x: -3,
        ease: Linear.easeInOut,
        onUpdate: () => {
          queueItem.controller.mesh.position.x = pos.x
        }
      }),
      0.0
    )

    timeline.add(
      gsap.to(pos, {
        duration: 1,
        x: 3,
        ease: Sine.easeInOut,
        onUpdate: () => {
          queueItem.controller.mesh.position.x = pos.x
        }
      }),
      0.7
    )

    timeline.add(() => {
      sound.play('coin-reveal-sprite', 0.2, 'variant' + Math.ceil(Math.random() * 5))?.rate(2 + Math.random() * 0.4)
    }, 1.4)

    timeline.add(
      gsap.to(pos, {
        duration: 1,
        x: Math.random() * 3 - 1.5,
        y: 8.5 + Math.random() * 3 - 1.5,
        ease: Sine.easeInOut,
        onUpdate: () => {
          queueItem.controller.mesh.position.x = pos.x
          queueItem.controller.mesh.position.y = pos.y
        }
      }),
      1.7
    )

    //rotate
    timeline.add(
      gsap.to(queueItem.controller.mesh.rotation, {
        duration: 1.8,
        x: Math.PI * 0.5,
        y: 0,
        ease: Back.easeOut
      }),
      0.3
    )

    timeline.add(
      gsap.to(queueItem.controller.mesh.rotation, {
        duration: 0.4,
        z: 0.6,
        ease: Sine.easeInOut
      }),
      0.5
    )

    timeline.add(
      gsap.to(queueItem.controller.mesh.rotation, {
        duration: 0.4,
        z: -0.3,
        x: Math.PI * 0.5 + (Math.random() * 0.4 - 0.2),
        ease: Sine.easeInOut
      }),
      0.9
    )

    timeline.add(() => {
      sound.startLoop('wind-chimes')
      this.onRevealItemAdded.invoke(queueItem.controller.asset)
    }, 1.0)

    timeline.add(
      gsap.to(queueItem.controller.mesh.rotation, {
        duration: 0.3,
        z: 0.1,
        x: Math.PI * 0.5,
        ease: Sine.easeInOut
      }),
      1.3
    )
  }

  activateRevealedControllers() {
    sound.stopLoop('wind-chimes')
    this.tokenControllers.forEach((tc, index) => {
      if (index < 10) {
        setTimeout(() => {
          sound
            .play('coin-reveal-sprite', 0.2, 'variant' + Math.ceil(Math.random() * 5))
            ?.rate(0.8 + Math.random() * 0.4)
        }, Math.random() * 0.3)
      }

      tc.removeGlowSprite().then(() => {
        if (tc.body.label.includes(BODY_LABEL_ADDON_REVEALED)) {
          this.physics.interactions.addToDropWaitingList(tc.body)
        }
      })
    })
  }

  setSlitLight(material: MeshBasicMaterial | null, slitPosition: Vector3): number {
    let factor = 0

    this.#slitLightDistanceLine.start.set(slitPosition.x - 2, slitPosition.y, PhysicsLayerZ.Front)
    this.#slitLightDistanceLine.end.set(slitPosition.x + 2, slitPosition.y, PhysicsLayerZ.Front)

    const dist = this.physics.distanceToNearestDraggedCoinFrom(this.#slitLightDistanceLine)
    factor = 1.0 - MathUtils.smoothstep(dist, 0, 3)
    factor *= factor * 2

    if (material && material.userData.shader) {
      const shader = material.userData.shader
      shader.uniforms.slitLit.value += (factor - shader.uniforms.slitLit.value) * 0.2
    }

    if (this.#nearSlitSound) {
      let currentVolume = this.#nearSlitSound!.volume()
      currentVolume += (factor * 0.2 - currentVolume) * 0.3
      const sound = container.resolve<ISound>(Interface.ISound)
      sound.setLoopVolume('slit_ambience_1', currentVolume)
    }

    return factor
  }

  fadeOutSlitLight(material: MeshBasicMaterial) {
    let currentVolume = this.#nearSlitSound!.volume()
    currentVolume += (0 - currentVolume) * 0.1
    this.#nearSlitSound?.volume(currentVolume)
    if (this.#nearSlitSound) {
      const sound = container.resolve<ISound>(Interface.ISound)
      sound.setLoopVolume('slit_ambience_1', currentVolume)
    }

    const shader = material.userData.shader
    if (shader && shader.uniforms.slitLit) {
      shader.uniforms.slitLit.value += (0 - shader.uniforms.slitLit.value) * 0.1
    }
  }

  getRandomPosition() {
    const rangeX = Math.abs(this.physics.visibleEdge.x * 2) * 0.7
    const rangeY = Math.abs(this.physics.visibleEdge.y) * 0.6

    return new Vector2(Math.random() * rangeX - rangeX * 0.5, Math.random() * rangeY - rangeY * 0.5 - rangeY * 0.7)
  }

  placeCoin(cc: BaseController, forceRandom = false) {
    const newPosition = new Vector2()
    let savedX = cc.asset.ux.user.posx

    const savedY = Math.max(Math.min(20, cc.asset.ux.user.posy), this.physics.visibleEdge.y * 0.9)

    if (!forceRandom && savedX && savedY && savedX !== 0 && savedY !== 0) {
      if (savedX < this.physics.visibleEdge.x * 0.8) {
        savedX = this.physics.visibleEdge.x * 0.8
      } else if (savedX > -this.physics.visibleEdge.x * 0.8) {
        savedX = -this.physics.visibleEdge.x * 0.8
      }

      newPosition.set(savedX, savedY)
    } else {
      //init position on load
      newPosition.copy(this.getRandomPosition())

      //check if coin already in place
      this.physics.bodies.forEach(otherBody => {
        if (Bounds.contains(otherBody.bounds, { x: newPosition.x, y: newPosition.y })) {
          newPosition.set(otherBody.bounds.max.x + 40, otherBody.bounds.max.y + 40)
        }
      })
    }

    cc.setPosition(newPosition.x, newPosition.y)

    if (cc.asset.t === TokenType.Coin && cc.asset.ux.user.rot !== 0 && cc.asset.ux.user.rot !== undefined) {
      Body.setAngle(cc.body, cc.asset.ux.user.rot)
    }

    this.physics.remove(cc.body)
    this.physics.add(cc.body)
  }

  addToSpawnWaitingList(
    controller: BaseController,
    point: Vector,
    velocity: Vector,
    spawnLocation: SpawnLocation,
    randomFactor = 1
  ) {
    controller.disable()
    controller.body.collisionFilter.group = CollisionGroup.ActiveCoins
    controller.body.collisionFilter.mask = CollisionCategory.None

    controller.setPosition(point.x, point.y)

    this.#spawnWaitingList.push({ controller, point, velocity, spawnLocation, randomFactor })
  }

  createTokenController = async (create: { asset: Asset; spawnLocation: SpawnLocation }): Promise<BaseController> => {
    let cc: BaseController | undefined

    const contentType = create.asset.c as ContentType

    if (create.asset.t === TokenType.Coin) {
      cc = new CoinController(create.asset, create.spawnLocation)
    } else if (create.asset.t === TokenType.Ting) {
      cc = new TingController(create.asset, create.spawnLocation)
    }

    if (cc) {
      await cc.load()
      this.addCoinControllerToWorld(cc)
    }

    return cc! as BaseController
  }

  hasTokensIncomingFromWalletId(walletId: string) {
    return this.tokenControllers.some(cc => cc.asset.ux.user.fromWalletId === walletId)
  }

  assetRemoveControllers(coinControllers: CoinController[]) {
    const copy = coinControllers.concat([])
    for (let c = 0; c < copy.length; c++) {
      this.assetRemoveControllerFromWorld(copy[c])
    }
    coinControllers.length = 0
    this.updateCachedLists()
  }

  assetRemoveController(tokenController: BaseController) {
    this.assetRemoveControllerFromWorld(tokenController)
    this.updateCachedLists()
  }

  assetRemoveByAssetId(id: Id) {
    const cc = this.getCoinControllerByAssetId(id)
    if (cc) {
      this.assetRemoveController(cc)
    }
  }

  private addCoinControllerToWorld = (cc: BaseController) => {
    this.tokenControllers.push(cc)
    //this.physics.add(cc.body)
    this.coinContainer.add(cc.mesh)
    this.placeCoinInScene(cc)
    this.updateCachedLists()
  }

  addMeshWithPhysics(mesh: Mesh, body: Body) {
    this.physics.add(body)
    this.coinContainer.add(mesh)
  }

  private assetRemoveControllerFromWorld(coinController: BaseController) {
    const i = this.tokenControllers.findIndex(c => c.id === coinController.id)
    if (i !== -1) {
      this.tokenControllers.splice(i, 1)

      this.physics.remove(coinController.body)
      coinController.destroy()
    }
  }

  protected placeCoinInScene(bc: BaseController): void {
    const mat = bc.mesh.material as MeshStandardMaterial
    if (mat) {
      //reset if room overridden this (monster wallet)
      mat.clipIntersection = false
    }
    bc.body.label = bc.body.label.replace(BODY_LABEL_ADDON_ANIMATION, '')
    //this.updateSaveGoals()

    if (bc.asset.va === ToolVariant.Savegoal) {
      if (!Object.hasOwn(bc.asset.ux.user, 'goal')) {
        this.openCoin(bc)
      }
    } else if (bc.asset.va === ToolVariant.Message) {
      if (!Object.hasOwn(bc.asset.ux, 'public') && !Object.hasOwn(bc.asset.ux.public, 'message')) {
        this.openCoin(bc)
      }
    }
  }

  private updateCachedLists() {
    this.coinMeshes = this.tokenControllers.map(coinController => coinController.mesh)
    this.onUpdateBloomList.invoke()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  update(time: number, delta: number): void {
    //if (!this.coinZoomedIn) {
    this.physics.update(time, delta)
    //}

    //this.camera.position.z = this.extraCameraOffsetZ

    this.particleManager?.update(time, delta)

    this.#zoomCoinBackgroundMaterial.uniforms.time.value = Math.sin(((time * 0.05) % 1) * Math.PI * 2.0 - Math.PI)

    const normalRotation = time * 0.05 //Math.sin() * Math.PI * 2.0) * 0.001

    this.tokenControllers.forEach(tokenController => {
      tokenController.update(time, delta)

      //rotate env map if no active reveal tokens
      if (
        tokenController.glowSprite ||
        (!tokenController.glowSprite && !this.tokenControllers.find(tc => tc.glowSprite))
      ) {
        const mat = tokenController.mesh.material as MeshStandardMaterial
        if (mat && mat.userData.shader && mat.userData.shader.uniforms.normalRotation) {
          mat.userData.shader.uniforms.normalRotation.value = normalRotation
        }
      }

      const mat2 = tokenController.mesh.material as ShaderMaterial
      if (mat2 && mat2.uniforms && mat2.uniforms.cameraPosition) {
        mat2.uniforms.cameraPosition.value.copy(this.cameraPivot.position)
      }

      if (tokenController.glowSprite && Math.random() > 0.7) {
        this.particleManager?.spawnParticle({
          position: tokenController.mesh.position
            .clone()
            .add(new Vector3(Math.random() * 2 - 1, Math.random() * 2 - 1, -2)),
          velocity: tempVector3.set(0, 0, -1),
          size: 2.5,
          lifetime: 2,
          color: tempColor.set(
            (tokenController as CoinController).baseColor ? (tokenController as CoinController).baseColor : 0xffffff
          ),
          velocityRandomness: 0.4,
          positionRandomness: 0.7,
          turbulence: 0.6
        })
      }

      /*if (tokenController.isZoomed && tokenController.spinSpeed > 0.01) {
        for (let i = 0; i < Math.max(1, coinController.spinSpeed * 70); i++) {
          this.particleManager?.spawnParticle({
            position: tempVector3.set(Math.random() * 2 - 1, Math.random() * 2 - 1 + this.config.roomHeight * 0.4, 0),
            velocity: tempVector3_2.set(0, 0, Math.max(10, tokenController.spinSpeed * 1000)),
            size: 4.5,
            lifetime: 2,
            color: tempColor.set(tokenController.baseColor),
            velocityRandomness: 0.4,
            positionRandomness: 0,
            turbulence: 0
          })
        }
      }*/
    })
  }

  renderOverlay() {}

  hideAllTubes(): Promise<void> {
    return Promise.resolve()
  }

  get isLoaded() {
    return this.#firstTimeActivated
  }

  getCoinControllerByDenomination(d: Denomination) {
    return this.tokenControllers
      .filter(c => c.asset.t === TokenType.Coin)
      .map(c => c as CoinController)
      .find(c => c.denomination === d)
  }

  getTokenControllerByBodyId(id: number) {
    return this.tokenControllers.find(c => c.body.id === id)
  }

  getCoinControllerByAssetId(id: Id) {
    return this.tokenControllers.find(c => c.assetId === id)
  }

  resize() {
    this.physics.resize()
  }

  destroy() {
    this.isDestroyed = true
    this.inactivate()

    if (this.envMapEditor) {
      this.envMapEditor.onEnvMapUpdated.remove(this.onEnvMapUpdated)
      this.envMapEditor.destroy()
    }

    this.onCoinZoomedIn.removeAll()
    this.physics.destroy()
    this.removeInteractionListeners()

    const allChilds: any = []
    this.scene.traverse(child => {
      allChilds.push(child)
    })

    allChilds.forEach((child: any) => {
      if (child.material) {
        Object.keys(child.material).forEach(prop => {
          if (!child.material[prop]) return
          if (child.material[prop] !== null && typeof child.material[prop].dispose === 'function') {
            child.material[prop].dispose()
          }
        })

        if (child.userData.bbMesh) {
          child.userData.bbMesh = undefined
        }
        if (child.userData.tween1) {
          child.userData.tween1.kill()
        }
        if (child.userData.tween2) {
          child.userData.tween2.kill()
        }

        child.material.dispose()
      }
      if (child.geometry) {
        ;(child.geometry as BufferGeometry).dispose()
      }
    })

    while (this.scene.children.length > 0) {
      this.scene.remove(this.scene.children[0])
    }
  }

  getCoinPositions() {
    return this.tokenControllers.map(cc => {
      const screenPos = SceneUtils.sceneToScreenSpace(this.camera, cc.mesh.position)
      return { pos: screenPos, asset: cc.asset }
    })
  }

  getTokensInScreenSpace(): { pos: Vector2; asset: Asset }[] {
    return this.tokenControllers
      .filter(
        tc =>
          tc.isLifted &&
          //tc.asset.t === TokenType.Ephemeral &&
          //tc.asset.c === ContentType.Tool &&
          tc.asset.va === ToolVariant.Savegoal
      )
      .map(tc => {
        const screenPos = SceneUtils.sceneToScreenSpace(this.camera, tc.mesh.position)
        return { pos: screenPos, asset: tc.asset }
      })
  }
}

export { RoomConfig }
